import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { useLoaderData } from "@tanstack/react-router"
import React, { useCallback, useEffect, useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form"

import AutoComplete from "@/components/common/Autocomplete/AutoComplete"
import { renderSelectPlaceholder } from "@/components/Forms"
import {
  DEFAULT_AUTOCOMPLETE_VALUE,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import { type Country, type Location } from "@/graphql/codegen/graphql"
import { useGetRegionList } from "@/screens/Products/components/Product/Information/hooks/useGetRegionList"
import type { ProductOriginFormSchema } from "@/screens/Products/components/Product/Information/schemas/ProductOriginSchema"
import { ProductOrigin } from "@/screens/Products/components/Product/Information/types/productOrigin"
import type { AutocompleteOption } from "@/types/form-fields"
import { formatLocationAddress } from "@/utils/utils"

type RegionFieldPath = "manufacturingOrigin.region" | "countryOrigin.region"

interface ProductOriginFormProps {
  title: string
  selectTypeLabel: string
  selectLocationLabel: string
  parentFieldName: "manufacturingOrigin" | "countryOrigin"
  testIdPrefix: string
}

export const ProductOriginForm: React.FC<ProductOriginFormProps> = ({
  title,
  selectTypeLabel,
  selectLocationLabel,
  parentFieldName,
  testIdPrefix,
}) => {
  const { control, setValue, watch } = useFormContext<ProductOriginFormSchema>()

  const { countries, companyLocations } = useLoaderData({
    from: "/app/product/$productId/edit/_layout/information",
  })

  const originType = watch(`${parentFieldName}.originType` as keyof ProductOriginFormSchema)
  const country = watch(`${parentFieldName}.country` as keyof ProductOriginFormSchema)
  const region = watch(`${parentFieldName}.region` as RegionFieldPath)

  const { regions, isRegionsFetching } = useGetRegionList(country as string)

  const regionOptionList = useMemo(() => {
    return (
      (regions?.map(({ name, isoCode }) => ({
        label: name,
        id: isoCode,
      })) as AutocompleteOption[]) ?? []
    )
  }, [regions])

  const handleSupplierLocationChange = (value: string) => {
    const location = companyLocations.find((loc: Location) => loc.locationId === value)
    setValue(`${parentFieldName}.city` as keyof ProductOriginFormSchema, location?.city ?? null, {
      shouldValidate: true,
    })
    setValue(`${parentFieldName}.country` as keyof ProductOriginFormSchema, location?.country?.isoCode ?? null, {
      shouldValidate: true,
    })
    setValue(`${parentFieldName}.exactLocation` as keyof ProductOriginFormSchema, value, { shouldValidate: true })
    setValue(
      `${parentFieldName}.region` as RegionFieldPath,
      { label: location.region?.name ?? "", id: location.region?.isoCode ?? "" },
      { shouldValidate: true }
    )
  }

  const handleGeneralLocationChange = useCallback(
    (field: any) => (event: any) => {
      field.onChange(event.target.value)
      setValue(`${parentFieldName}.exactLocation` as keyof ProductOriginFormSchema, null)
    },
    [parentFieldName, setValue]
  )

  const handleRegionChange = (value: string) => {
    if (!regionOptionList.length) {
      return
    }

    const region = regionOptionList.find((option) => option.label === value)
    if (!region) {
      return
    }
    setValue(`${parentFieldName}.region` as RegionFieldPath, { label: region?.label || "", id: region?.id || "" })
  }

  useEffect(() => {
    if (typeof region === "string" && regionOptionList.length) {
      const regionOption = regionOptionList.find((option) => option.label === region)
      setValue(`${parentFieldName}.region` as RegionFieldPath, {
        id: regionOption?.id ?? "",
        label: regionOption?.label ?? "",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentFieldName, regionOptionList, setValue])

  return (
    <>
      <Typography variant='body1' fontWeight={500} className='leading-6 tracking-[0.15px] text-gray-700'>
        {title}
      </Typography>

      <Controller
        name={`${parentFieldName}.originType` as keyof ProductOriginFormSchema}
        control={control}
        render={({ field }) => (
          <FormControl component='fieldset' margin='normal'>
            <label className={FIELD_LABEL_CLASSES} id='origin-type'>
              {selectTypeLabel}
            </label>
            <RadioGroup {...field} row data-testid={`${testIdPrefix}-type`}>
              <FormControlLabel value={ProductOrigin.SUPPLIER} control={<Radio />} label='Exact Location' />
              <FormControlLabel value={ProductOrigin.OTHER} control={<Radio />} label='General Location' />
            </RadioGroup>
          </FormControl>
        )}
      />

      {originType === ProductOrigin.SUPPLIER && (
        <Controller
          name={`${parentFieldName}.exactLocation` as keyof ProductOriginFormSchema}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <label className={FIELD_LABEL_CLASSES} id={`${testIdPrefix}-exact-location-select`}>
                {selectLocationLabel} <span className='text-red-600'>*</span>
              </label>
              <Select
                {...SELECT_STYLING_PROPS}
                labelId={`${testIdPrefix}-exact-location-select`}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography color='textDisabled'>Select One</Typography>
                  }

                  const location = companyLocations.find((loc: Location) => loc.locationId === selected)

                  return formatLocationAddress(location)
                }}
                value={typeof field.value === "string" ? field.value : ""}
                onChange={(event) => handleSupplierLocationChange(event.target.value as string)}
                data-testid={`${testIdPrefix}-exact-location`}
              >
                {companyLocations.map((location: Location) => (
                  <MenuItem key={location.locationId} value={location.locationId}>
                    <Box className='overflow-auto text-wrap'>{formatLocationAddress(location)}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )}

      {originType === ProductOrigin.OTHER && (
        <>
          <FormControl fullWidth margin='normal'>
            <Controller
              name={`${parentFieldName}.country` as keyof ProductOriginFormSchema}
              control={control}
              render={({ field }) => (
                <>
                  <label
                    className={FIELD_LABEL_CLASSES}
                    id={`${testIdPrefix}-country-select`}
                    htmlFor={`${testIdPrefix}-country-select-input`}
                  >
                    Country
                  </label>
                  <Select
                    {...SELECT_STYLING_PROPS}
                    labelId={`${testIdPrefix}-country-select`}
                    data-testid={`${testIdPrefix}-country-select`}
                    renderValue={renderSelectPlaceholder({
                      field: { ...field, value: typeof field.value === "string" ? field.value : "" },
                    })}
                    value={typeof field.value === "string" ? field.value : ""}
                    onChange={handleGeneralLocationChange(field)}
                  >
                    {countries.map((country: Country) => (
                      <MenuItem key={country.isoCode} value={country.isoCode}>
                        <Box className='overflow-hidden text-ellipsis'>{country.name}</Box>
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            />
          </FormControl>

          <Box className='flex w-full flex-row gap-4'>
            <FormControl className='w-1/2'>
              <AutoComplete<AutocompleteOption>
                onInputChange={handleRegionChange}
                label='State/Region'
                placeholder='Select One'
                id={`${testIdPrefix}-region-select`}
                options={regionOptionList}
                loading={isRegionsFetching}
                name={`${parentFieldName}.region`}
                control={control}
                defaultEmptyState={DEFAULT_AUTOCOMPLETE_VALUE}
                freeSolo
                getOptionLabel={(option) => option?.label ?? option}
                data-testid={`${testIdPrefix}-region-select`}
                noFirstValidate
              />
            </FormControl>

            <FormControl className='w-1/2'>
              <Controller
                name={`${parentFieldName}.city` as keyof ProductOriginFormSchema}
                control={control}
                render={({ field }) => (
                  <Box className='relative'>
                    <label
                      className={FIELD_LABEL_CLASSES}
                      id={`${testIdPrefix}-city-input`}
                      htmlFor={`${testIdPrefix}-city-input`}
                    >
                      City
                    </label>
                    <TextField
                      {...field}
                      {...TEXT_FIELD_STYLING_PROPS}
                      placeholder='City'
                      data-testid={`${testIdPrefix}-city-input`}
                      onChange={handleGeneralLocationChange(field)}
                      disabled={!country}
                    />
                  </Box>
                )}
              />
            </FormControl>
          </Box>
        </>
      )}
    </>
  )
}
